import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import type { WidgetEntity } from 'src/entities/widget/WidgetEntity';
import type { TSerializedWidget } from 'src/entities/workspace/types';
import { requireService } from 'src/packages/di';

/**
 * Host-adjacent reference-wells persistence (not entity fields).
 *
 * Call from a type-specific `serializeX` after building the widget payload:
 *   return mergeReferenceWellsIntoSerializedWidget(serialized, widget);
 *
 * Pair with `extractAndStashReferenceWellsTree` in the matching `mapX`.
 * Runtime store must expose `getReferenceWellsSnapshot()`; mount hydrates via
 * `takePendingReferenceWellsTree(uuid)`.
 *
 * MobX: reading the store/snapshot here (during workspaceState) tracks selection
 * changes when WidgetStoreService uses an observable registry.
 */
export function mergeReferenceWellsIntoSerializedWidget(
  serialized: TSerializedWidget,
  widget: WidgetEntity,
): TSerializedWidget {
  const widgetStore = requireService('widgetStoreService').getStore<WidgetEntity, IWidgetStore>(widget);
  const snapshot = widgetStore?.getReferenceWellsSnapshot?.();

  if (snapshot === undefined) {
    return serialized;
  }

  return {
    ...serialized,
    internalState: {
      ...serialized.internalState,
      referenceWellsTree: snapshot,
    },
  };
}
